import { skipToken } from '@reduxjs/toolkit/dist/query'
import { mdiArrowRight } from '@mdi/js'
import { dealerMonitorEquipment } from '@racepointenergy/swc/scs/dealer-monitor-equipment'
import { equipmentEntityAdapter } from '@racepointenergy/swc/scs/features/host-equipment'
import { useAppSelector } from 'app/hooks'
import ROUTES from 'app/routes'
import CommonLinkButton from 'components/Button/CommonLinkButton'
import { BodyPrimary } from 'components/Typography'
import { selectedHomeSelector } from 'features/home/selectors'
import { currentRootDomainIdSelector } from 'features/rootDomains/selectors'
import AvailabilityChecker from 'features/home/SysMonGuard/AvailabilityChecker'
import { useCallback, useMemo } from 'react'
import { generatePath, useHistory } from 'react-router-dom'
import { StyledIcon, SubInfoBlackCardDate } from '../../styles'
import {
  HostInfoContainer,
  HostInfoHeaderContainer,
  HostInfoWrapper,
  HostTypeLabelContainer,
} from './styles'
import { DASH_SYMBOL } from 'app/consts'
import { useIntl } from 'react-intl'
import { homeStatusHostInfoMessages } from './messages'
import {
  hostNameByEnum,
  hostNameByHardwareModel,
  hostNameSecondaryByEnum,
} from './hostType'

function HostInfo() {
  const home = useAppSelector(selectedHomeSelector.data)
  const currentRootId = useAppSelector(currentRootDomainIdSelector)
  const { formatMessage } = useIntl()
  const history = useHistory()
  const equipmentInitialState = equipmentEntityAdapter.getInitialState()

  const canQueryHost = !!home?.id
  const { data = equipmentInitialState, isFetching } =
    dealerMonitorEquipment.useQuery(
      canQueryHost ? { id: home?.id! } : skipToken,
      { skip: !canQueryHost }
    )

  const homeDevicePath = useMemo(() => {
    if (data && home) {
      const { entities = {}, ids } = data
      const id = ids.find(
        (id: any) =>
          (entities[id]?.StatusPathParts.type === 'Chassis' ||
            entities[id]?.StatusPathParts.type === 'Hosts') &&
          entities[id]?.StatusPathParts.name === home.name
      )
      if (id) {
        return entities[id]?.StatusPath
      }
    }
  }, [data, home])

  const hostName = useMemo(() => {
    if (!home?.hostInfo) {
      return null
    }

    const machineType = home.hostInfo.hostMachineType
    if (machineType === 0 || machineType === 7) {
      return hostNameByHardwareModel(home.hostInfo.hostHardwareModel || '')
    }

    return [hostNameByEnum[machineType], hostNameSecondaryByEnum[machineType]]
  }, [home?.hostInfo])

  const handleClickToHostInfoHeader = useCallback(() => {
    if (currentRootId && homeDevicePath) {
      const devicePath = generatePath(ROUTES.homeDevice, {
        id: homeDevicePath,
        homeId: home?.id,
        rootDomainId: currentRootId,
      })
      return history.push(devicePath)
    }
  }, [currentRootId, history, home?.id, homeDevicePath])
  if (!home?.id) return null

  return (
    <AvailabilityChecker homeId={home.id}>
      {({ errors, isAvailabilityLoading }) => {
        return (
          <HostInfoContainer>
            <HostInfoHeaderContainer>
              {formatMessage(homeStatusHostInfoMessages.hostInfo)}
              {!isAvailabilityLoading && !isFetching && errors.length === 0 && (
                <CommonLinkButton onClick={handleClickToHostInfoHeader}>
                  <StyledIcon path={mdiArrowRight} size={1} />
                </CommonLinkButton>
              )}
            </HostInfoHeaderContainer>
            <HostInfoWrapper>
              <BodyPrimary>
                {formatMessage(homeStatusHostInfoMessages.hostType)}
              </BodyPrimary>
              <BodyPrimary>
                <HostTypeLabelContainer>
                  <b>{hostName?.[0] || DASH_SYMBOL}</b>
                  {hostName?.[1]}
                </HostTypeLabelContainer>
              </BodyPrimary>
            </HostInfoWrapper>
            <HostInfoWrapper>
              <BodyPrimary>
                {formatMessage(homeStatusHostInfoMessages.OSVersion)}
              </BodyPrimary>
              <BodyPrimary>
                <SubInfoBlackCardDate>
                  <b>{home?.hostInfo?.OSVersion || DASH_SYMBOL}</b>
                </SubInfoBlackCardDate>
              </BodyPrimary>
            </HostInfoWrapper>
          </HostInfoContainer>
        )
      }}
    </AvailabilityChecker>
  )
}

export default HostInfo
